import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { db, isTeacher, resolveTeacherClassId, Teacher } from '../../../utils/firebase';
import { DEFAULT_VIRTUAL_PET_SETTINGS } from '../student/VirtualPet/config/petBalance';
import { ORIGINAL_PET_ASSETS } from '../student/VirtualPet/config/evolutionAssets';
import { getEvolutionStatus } from '../student/VirtualPet/domain/petEngine';
import type { OriginalPetForm, PetEvolutionState, PetProfile, VirtualPetClassSettings } from '../student/VirtualPet/types';

interface StudentPetRow {
  id: string;
  name: string;
  number: number;
  profile: PetProfile | null;
  evolution: PetEvolutionState;
}

const formNames: Record<OriginalPetForm, string> = {
  starEgg: '별알', mongsil: '몽실', starBean: '별콩', cloudBean: '구름콩', flameBean: '불꽃콩',
  starKeeper: '별지기', cloudGuardian: '구름수호자', flameDragon: '불꽃용'
};

const fieldStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: '1px solid #E5E7EB', borderRadius: 10,
  padding: '10px 12px', fontSize: 15, background: '#fff'
};

const clampInteger = (value: unknown, min: number, max: number) =>
  Math.min(max, Math.max(min, Math.round(Number(value) || 0)));

const todayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const emptyEvolution: PetEvolutionState = { dungeonClearCount: 0, history: [] };

const toDate = (value: unknown): Date => {
  if (value instanceof Date) return value;
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate();
  return new Date();
};

const VirtualPetManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  const classId = useMemo(() => resolveTeacherClassId(teacher), [teacher]);
  const basePath = teacher?.schoolId && classId ? `schools/${teacher.schoolId}/classes/${classId}` : '';
  const [settings, setSettings] = useState<VirtualPetClassSettings>(DEFAULT_VIRTUAL_PET_SETTINGS);
  const [students, setStudents] = useState<StudentPetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!basePath) { setLoading(false); return; }
    setLoading(true);
    try {
      const [settingsSnap, studentSnap] = await Promise.all([
        getDoc(doc(db, `${basePath}/virtualPetSettings/config`)),
        getDocs(collection(db, `${basePath}/students`))
      ]);
      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as Partial<VirtualPetClassSettings>;
        setSettings({ ...DEFAULT_VIRTUAL_PET_SETTINGS, ...data });
      }
      const rows = await Promise.all(studentSnap.docs.map(async (studentDoc) => {
        const data = studentDoc.data();
        const [profileSnap, evolutionSnap] = await Promise.all([
          getDoc(doc(db, `${basePath}/students/${studentDoc.id}/virtualPet/profile`)),
          getDoc(doc(db, `${basePath}/students/${studentDoc.id}/virtualPet/evolution`))
        ]);
        const evolutionData = evolutionSnap.exists() ? evolutionSnap.data() : null;
        return {
          id: studentDoc.id,
          name: String(data.name || '이름 없음'),
          number: Number(data.number ?? data.studentNumber ?? 0),
          profile: profileSnap.exists() ? {
            ...(profileSnap.data() as PetProfile),
            abilities: {
              starlight: Number(profileSnap.data().abilities?.starlight || 1),
              vitality: Number(profileSnap.data().abilities?.vitality || 1),
              courage: Number(profileSnap.data().abilities?.courage || 1)
            }
          } : null,
          evolution: evolutionData ? {
            dungeonClearCount: Number(evolutionData.dungeonClearCount || 0),
            history: Array.isArray(evolutionData.history) ? evolutionData.history.map((item: { from: OriginalPetForm; to: OriginalPetForm; at?: unknown }) => ({
              from: item.from, to: item.to, at: toDate(item.at)
            })) : [],
            lastEvolvedAt: evolutionData.lastEvolvedAt ? toDate(evolutionData.lastEvolvedAt) : undefined
          } : emptyEvolution
        };
      }));
      rows.sort((a, b) => a.number - b.number || a.name.localeCompare(b.name));
      setStudents(rows);
    } catch (error) {
      console.error(error);
      toast.error('가상펫 관리 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  useEffect(() => { void load(); }, [load]);

  const saveSettings = async () => {
    if (!basePath) return;
    setSaving(true);
    const normalized: VirtualPetClassSettings = {
      enabled: settings.enabled,
      freeChatPerDay: clampInteger(settings.freeChatPerDay, 0, 20),
      freeRestPerDay: clampInteger(settings.freeRestPerDay, 0, 10),
      freeStrokePerDay: clampInteger(settings.freeStrokePerDay, 0, 20),
      interactionCooldownSec: clampInteger(settings.interactionCooldownSec, 0, 300)
    };
    try {
      await setDoc(doc(db, `${basePath}/virtualPetSettings/config`), {
        ...normalized, updatedAt: serverTimestamp(), updatedBy: currentUser?.uid || null
      }, { merge: true });
      setSettings(normalized);
      toast.success('가상펫 운영 설정을 저장했습니다.');
    } catch (error) {
      console.error(error);
      toast.error('설정을 저장하지 못했습니다.');
    } finally { setSaving(false); }
  };

  const resetPet = async (student: StudentPetRow) => {
    if (!basePath || !window.confirm(`${student.name} 학생의 가상펫 성장 기록과 보유 펫 아이템을 초기화할까요?\n이 작업은 되돌릴 수 없습니다.`)) return;
    setResettingId(student.id);
    try {
      const petBase = `${basePath}/students/${student.id}/virtualPet`;
      const runs = await getDocs(collection(db, `${petBase}/dungeonRuns`));
      const batch = writeBatch(db);
      runs.docs.forEach((run) => batch.delete(run.ref));
      ['profile', 'stats', 'inventory', 'evolution'].forEach((id) => batch.delete(doc(db, `${petBase}/${id}`)));
      await batch.commit();
      await deleteDoc(doc(db, `${petBase}/dungeonRuns`)).catch(() => undefined);
      setStudents((prev) => prev.map((row) => row.id === student.id ? { ...row, profile: null, evolution: emptyEvolution } : row));
      toast.success(`${student.name} 학생의 가상펫을 초기화했습니다.`);
    } catch (error) {
      console.error(error);
      toast.error('가상펫 초기화에 실패했습니다.');
    } finally { setResettingId(null); }
  };

  if (!currentUser || !isTeacher(currentUser) || !basePath) {
    return <div style={{ padding: 32 }}>담임 학급 정보가 있어야 가상펫을 관리할 수 있습니다.</div>;
  }

  const activeCount = students.filter((student) => student.profile).length;

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: '32px 20px 64px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>가상펫 관리</h1>
        <p style={{ margin: '8px 0 0', color: '#6B7280' }}>학급 운영 규칙과 학생별 성장 현황을 한곳에서 관리합니다.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[['재학생', `${students.length}명`], ['가상펫 시작', `${activeCount}명`], ['아직 시작 전', `${students.length - activeCount}명`]].map(([label, value]) => (
          <Card key={label}><div style={{ padding: 18 }}><div style={{ color: '#6B7280', fontSize: 13 }}>{label}</div><div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{value}</div></div></Card>
        ))}
      </div>

      <Card>
        <div style={{ padding: 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div><h2 style={{ margin: 0, fontSize: 20 }}>학급 운영 설정</h2><p style={{ margin: '5px 0 0', color: '#6B7280', fontSize: 13 }}>학생 화면을 새로 열거나 새로고침하면 적용됩니다.</p></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontWeight: 600 }}>
              <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings((prev) => ({ ...prev, enabled: e.target.checked }))} />
              {settings.enabled ? '운영 중' : '이용 중지'}
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
            {([
              ['freeChatPerDay', '하루 대화 횟수', 20], ['freeRestPerDay', '하루 휴식 횟수', 10],
              ['freeStrokePerDay', '하루 무료 쓰다듬기', 20], ['interactionCooldownSec', '상호작용 간격(초)', 300]
            ] as const).map(([key, label, max]) => (
              <label key={key} style={{ display: 'grid', gap: 7, fontSize: 13, fontWeight: 600 }}>{label}
                <input style={fieldStyle} type="number" min={0} max={max} value={settings[key]} onChange={(e) => setSettings((prev) => ({ ...prev, [key]: Number(e.target.value) }))} />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}><Button variant="primary" onClick={saveSettings} disabled={saving}>{saving ? '저장 중…' : '설정 저장'}</Button></div>
        </div>
      </Card>

      <Card>
        <div style={{ padding: 22, marginTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}><h2 style={{ margin: 0, fontSize: 20 }}>학생별 성장 현황</h2><Button variant="outline" size="small" onClick={load}>새로고침</Button></div>
          {loading ? <div style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>불러오는 중…</div> : students.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>등록된 학생이 없습니다.</div> : (
            <div style={{ display: 'grid', gap: 9 }}>
              {students.map((student) => {
                const profile = student.profile;
                const form = profile?.petForm && profile.petForm in ORIGINAL_PET_ASSETS ? profile.petForm : 'starEgg';
                const daily = profile?.dailyActions?.date === todayKey() ? profile.dailyActions : undefined;
                const evolutionStatus = profile ? getEvolutionStatus(profile, student.evolution) : null;
                const isExpanded = expandedId === student.id;
                return <div key={student.id} style={{ border: '1px solid #EEF0F3', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(130px, 1.3fr) repeat(4, minmax(70px, .7fr)) auto', gap: 12, alignItems: 'center', padding: '11px 12px', overflowX: 'auto' }}>
                    <img src={ORIGINAL_PET_ASSETS[form].image} alt="" style={{ width: 46, height: 46, objectFit: 'contain', imageRendering: 'pixelated' }} />
                    <div><div style={{ fontWeight: 700 }}>{student.number ? `${student.number}번 ` : ''}{student.name}</div><div style={{ fontSize: 12, color: '#6B7280' }}>{profile ? `${profile.name} · ${formNames[form]}` : '아직 시작 전'}</div></div>
                    <div><small style={{ color: '#6B7280' }}>레벨</small><div style={{ fontWeight: 700 }}>{profile?.level ?? '-'}</div></div>
                    <div><small style={{ color: '#6B7280' }}>친밀도</small><div style={{ fontWeight: 700 }}>{profile?.intimacy ?? '-'}</div></div>
                    <div><small style={{ color: '#6B7280' }}>오늘 활동</small><div style={{ fontWeight: 700 }}>{daily ? (daily.chatCount + daily.restCount + daily.strokeCount) : 0}회</div></div>
                    <div><small style={{ color: '#6B7280' }}>던전 완료</small><div style={{ fontWeight: 700 }}>{student.evolution.dungeonClearCount}회</div></div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="outline" size="small" disabled={!profile} onClick={() => setExpandedId(isExpanded ? null : student.id)}>{isExpanded ? '접기' : '진화 보기'}</Button>
                      <Button variant="outline" size="small" disabled={!profile || resettingId === student.id} onClick={() => void resetPet(student)}>{resettingId === student.id ? '초기화 중…' : '초기화'}</Button>
                    </div>
                  </div>
                  {isExpanded && profile && evolutionStatus && (
                    <div style={{ borderTop: '1px solid #EEF0F3', background: '#FAFAFC', padding: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                        <strong>진화 단계</strong>
                        {student.evolution.history.map((item, index) => (
                          <React.Fragment key={`${item.from}-${item.to}-${index}`}>
                            {index === 0 && <span style={{ color: '#6B7280' }}>{formNames[item.from]}</span>}
                            <span style={{ color: '#9CA3AF' }}>→</span>
                            <span style={{ fontWeight: item.to === form ? 700 : 500 }}>{formNames[item.to]}</span>
                          </React.Fragment>
                        ))}
                        {student.evolution.history.length === 0 && <span style={{ fontWeight: 700 }}>{formNames[form]}</span>}
                      </div>
                      {evolutionStatus.nextForm ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <img src={ORIGINAL_PET_ASSETS[evolutionStatus.nextForm].image} alt="" style={{ width: 54, height: 54, objectFit: 'contain', imageRendering: 'pixelated' }} />
                            <div><div style={{ fontSize: 13, color: '#6B7280' }}>다음 예상 진화</div><div style={{ fontSize: 18, fontWeight: 700 }}>{formNames[evolutionStatus.nextForm]} {evolutionStatus.satisfied && <span style={{ color: '#16A34A', fontSize: 13 }}>· 진화 가능</span>}</div></div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                            {[
                              ['레벨', profile.level, evolutionStatus.requiredLevel],
                              ['친밀도', profile.intimacy, evolutionStatus.requiredIntimacy],
                              ...(Number(evolutionStatus.requiredDungeonClearCount) > 0 ? [['던전 완료', student.evolution.dungeonClearCount, Number(evolutionStatus.requiredDungeonClearCount)] as [string, number, number]] : [])
                            ].map(([label, current, required]) => {
                              const met = Number(current) >= Number(required);
                              return <div key={String(label)} style={{ background: '#fff', border: `1px solid ${met ? '#BBF7D0' : '#E5E7EB'}`, borderRadius: 10, padding: '10px 12px' }}>
                                <div style={{ fontSize: 12, color: '#6B7280' }}>{label}</div><div style={{ fontWeight: 700, color: met ? '#15803D' : '#374151' }}>{current} / {required} {met ? '✓' : ''}</div>
                              </div>;
                            })}
                          </div>
                          {form === 'mongsil' && <div style={{ marginTop: 12, color: '#6B7280', fontSize: 13 }}>현재 성장 성향: 유대 {profile.nurtureTraits?.bond ?? 0} · 돌봄 {profile.nurtureTraits?.care ?? 0} · 용기 {profile.nurtureTraits?.courage ?? 0} → <strong>{formNames[evolutionStatus.nextForm]}</strong> 예상</div>}
                          <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: '#F7F4FF', fontSize: 13 }}>
                            <strong>능력치</strong> · ✨ 별빛 {profile.abilities.starlight.toFixed(1)} · 🌿 생명력 {profile.abilities.vitality.toFixed(1)} · 🔥 용기 {profile.abilities.courage.toFixed(1)}
                          </div>
                        </>
                      ) : <div style={{ color: '#7C3AED', fontWeight: 700 }}>최종 진화를 완료한 펫입니다.</div>}
                    </div>
                  )}
                </div>;
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default VirtualPetManagement;
